import React from 'react';
import { useColorContext } from '../../../context/colorContext';

/**
 * StyledActionCard - A button styled like the PlynoteHome action cards, for reuse.
 * Props:
 * - icon: React element (icon)
 * - title: string (main label)
 * - desc: string (secondary description)
 * - highlight: boolean (special accent border/shadow)
 * - onClick: function
 * - style: object (optional extra styles)
 */
const StyledActionCard = ({
  icon,
  title,
  desc,
  highlight = false,
  onClick,
  style = {},
  titleStyle = {},
  descStyle = {},
  baseShadow,
  hoverShadow,
  hoverTranslateY = -3,
  ...props
}) => {
  const { bgSecondary, textPrimary, textSecondary, activeAccent } = useColorContext();
  const resolvedBaseShadow = baseShadow || style.boxShadow || (
    highlight
      ? `0 0 0 1px ${activeAccent}80, 0 8px 20px rgba(0,0,0,0.3)`
      : '0 4px 12px rgba(0,0,0,0.2)'
  );
  const resolvedHoverShadow = hoverShadow || (
    highlight
      ? `0 16px 34px rgba(0,0,0,0.34), 0 0 0 1px ${activeAccent}`
      : `0 12px 28px rgba(0,0,0,0.28), 0 0 0 1px ${activeAccent}55`
  );

  return (
    <div
      tabIndex={0}
      role="button"
      onClick={onClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick?.(e)}
      style={{
        backgroundColor: bgSecondary,
        borderRadius: '1.5rem',
        padding: '1.8rem 1.5rem',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        boxShadow: resolvedBaseShadow,
        border: highlight ? `1px solid ${activeAccent}` : 'none',
        outline: 'none',
        transform: 'translateY(0)',
        willChange: 'transform, box-shadow',
        ...style,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = `translateY(${hoverTranslateY}px)`;
        e.currentTarget.style.boxShadow = resolvedHoverShadow;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = resolvedBaseShadow;
      }}
      {...props}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        {icon}
        <h3 style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 500, margin: 0, ...titleStyle }}>{title}</h3>
      </div>
      <p style={{ color: textSecondary, fontSize: '0.9rem', lineHeight: 1.4, margin: 0, ...descStyle }}>{desc}</p>
    </div>
  );
};

export default StyledActionCard;
